/**
 * FilmStudioIntroModal — first-login feature announcement for the
 * Film Studio launch. Fires once per user on dashboard mount when
 * the `film_studio_intro_seen` user_data flag is unset.
 *
 * UX:
 *   - Multi-step modal that walks through Film Studio capabilities
 *   - Each step has an icon, a headline, and body copy
 *   - "Next" advances; "Open Film Studio" on the last step navigates
 *     to #/film and dismisses
 *   - "Maybe later" / close (X) at any step skips the rest and
 *     marks the flag so it never fires again
 *   - The flag persists to user_data so it survives logout/login
 *     and only fires once per coach, not once per session
 *
 * Triggered from BAMFull.jsx via a useEffect that checks the flag
 * once auth + onboarding complete.
 */
import React, { useEffect, useState } from "react";
import { Film, FileText, Camera, Layers, ChevronRight, X, Sparkles } from "lucide-react";
import { GOLD } from "../constants/theme";
import { saveUserData } from "../utils/userData";
import { logEvent } from "../utils/analytics";

const STORAGE_KEY = "film_studio_intro_seen";

const STEPS = [
  {
    Icon: Sparkles,
    eyebrow: "New on the platform",
    title: "BAM Film Studio is live",
    body: "Your private film room, built for coaches. Drop in YouTube links or upload your own footage. Tag, annotate, and break down film the way you actually coach.",
  },
  {
    Icon: FileText,
    eyebrow: "Step 1 of 3",
    title: "Notes that stick to the moment",
    body: "Pause the clip, type a note, and it anchors to that exact timestamp. Scrub the timeline and your notes follow along. Build a library of game film, player studies, and clips you can search and revisit anytime.",
  },
  {
    Icon: Camera,
    eyebrow: "Step 2 of 3",
    title: "Snapshots for the playbook",
    body: "Grab a frame from any clip with one click. Drop it into a session plan, share it with a player, or build a visual breakdown that lives outside the video.",
  },
  {
    Icon: Layers,
    eyebrow: "Step 3 of 3",
    title: "AFS possession breakdown",
    body: "Tag every possession with the Advantage Flow System. Track tiers, scan the full game by quality of attack, and find the patterns the box score misses.",
  },
  {
    Icon: Film,
    eyebrow: "Ready when you are",
    title: "Let's get you in there",
    body: "Click below to open Film Studio. Drop in a YouTube link to your last game and you'll see what it can do in under a minute.",
    isFinal: true,
  },
];

export function FilmStudioIntroModal({ onClose }) {
  const [step, setStep] = useState(0);
  const data = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const Icon = data.Icon;

  // Fire a single "viewed" event on first paint so we can track how
  // many coaches see the modal vs how many actually open Film Studio.
  useEffect(() => {
    logEvent("film_studio_intro_view", {});
  }, []);

  const persistSeen = async () => {
    try { await saveUserData(STORAGE_KEY, true).catch(() => {}); } catch {}
    try { localStorage.setItem(`bam_${STORAGE_KEY}`, "1"); } catch {}
  };

  const finishAndOpenFilm = async () => {
    logEvent("film_studio_intro_open_film", { from_step: step });
    await persistSeen();
    window.location.hash = "#/film";
    onClose && onClose();
  };

  const dismiss = async () => {
    logEvent("film_studio_intro_dismiss", { at_step: step });
    await persistSeen();
    onClose && onClose();
  };

  const next = () => {
    if (isLast) return finishAndOpenFilm();
    setStep((s) => s + 1);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 5000,
      background: "rgba(6,6,6,0.84)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
      fontFamily: "'DM Sans', sans-serif",
      animation: "fadeIn .25s ease",
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes glyphSweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      <div style={{
        maxWidth: 520, width: "100%",
        background: "linear-gradient(180deg, #111111, #0A0A0A)",
        border: `1px solid ${GOLD}55`,
        borderRadius: 18,
        boxShadow: `0 36px 90px rgba(0,0,0,0.75), inset 0 1px 0 ${GOLD}22`,
        padding: "32px 28px 22px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Animated gold sweep along the top edge — same touch as the
            masterclass deck cards. Subtle motion that says "this is new". */}
        <div aria-hidden="true" style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`,
          animation: "glyphSweep 6s ease-in-out infinite",
        }}/>

        {/* Close (top-right) */}
        <button onClick={dismiss} aria-label="Close" style={{
          position: "absolute", top: 12, right: 12,
          background: "transparent", border: "none", cursor: "pointer",
          padding: 6, display: "flex", alignItems: "center", justifyContent: "center",
          color: "#6C6658",
        }}>
          <X size={16}/>
        </button>

        <div key={step} style={{animation: "slideUp .28s ease both"}}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `${GOLD}18`, border: `1px solid ${GOLD}55`,
            display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: 18,
          }}>
            <Icon size={24} color={GOLD} strokeWidth={2}/>
          </div>

          <div style={{
            fontSize: 10, fontWeight: 800, color: GOLD,
            letterSpacing: 1.6, textTransform: "uppercase", marginBottom: 8,
          }}>{data.eyebrow}</div>
          <h2 style={{
            fontSize: 24, fontWeight: 800, color: "#F5F0E8",
            margin: "0 0 12px", lineHeight: 1.25, letterSpacing: -0.3,
          }}>{data.title}</h2>
          <p style={{
            fontSize: 14, lineHeight: 1.65, color: "#A09A8B",
            margin: "0 0 22px",
          }}>{data.body}</p>
        </div>

        {/* Step dots */}
        <div style={{
          display: "flex", justifyContent: "center", gap: 6,
          marginBottom: 18,
        }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 22 : 6, height: 6, borderRadius: 4,
              background: i === step ? GOLD : i < step ? `${GOLD}66` : "rgba(255,255,255,0.10)",
              transition: "width .25s ease, background .25s ease",
            }}/>
          ))}
        </div>

        {/* Footer actions */}
        <div style={{display: "flex", gap: 10, alignItems: "center"}}>
          <button onClick={dismiss} style={{
            flexShrink: 0,
            background: "transparent", border: "none",
            color: "#6C6658", fontSize: 12, fontWeight: 700,
            cursor: "pointer", padding: "10px 12px",
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {isLast ? "Maybe later" : "Skip"}
          </button>
          <div style={{flex: 1}}/>
          <button onClick={next} style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "11px 22px", borderRadius: 10,
            background: GOLD, color: "#0A0A0A",
            fontSize: 13, fontWeight: 800, letterSpacing: 0.3,
            border: "none", cursor: "pointer",
            boxShadow: `0 6px 18px ${GOLD}55`,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {isLast ? "Open Film Studio" : "Next"} <ChevronRight size={14} strokeWidth={2.5}/>
          </button>
        </div>
      </div>
    </div>
  );
}

export default FilmStudioIntroModal;
